import React, { useCallback, useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  EditRounded,
  DeleteRounded,
  MoreVertRounded,
} from "@mui/icons-material";
import { dt } from "../../../shared/themes/redesignTokens";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Thread } from "../midasPageModels";

// ═══════════════════════════════════════════════════════════════
//  Design Tokens
// ═══════════════════════════════════════════════════════════════

const midas = {
  gold: "#D4A843",
  goldSoft: "rgba(212, 168, 67, 0.06)",
  goldBorder: "rgba(212, 168, 67, 0.3)",
  inactiveIcon: "#C0C5CC",
  inactiveText: "#4B5563",
  timestampColor: "#C0C5CC",
  emptyText: "#C0C5CC",
};

/** Small text-lines SVG icon (3 horizontal lines, 14x14) */
const ThreadIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
    <line x1="2" y1="4" x2="12" y2="4" stroke={active ? midas.gold : midas.inactiveIcon} strokeWidth="1.4" strokeLinecap="round" />
    <line x1="2" y1="7" x2="10" y2="7" stroke={active ? midas.gold : midas.inactiveIcon} strokeWidth="1.4" strokeLinecap="round" />
    <line x1="2" y1="10" x2="8" y2="10" stroke={active ? midas.gold : midas.inactiveIcon} strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

/** Small plus SVG icon (12x12) for new conversation button */
const PlusIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
    <line x1="6" y1="2" x2="6" y2="10" stroke={midas.gold} strokeWidth="1.4" strokeLinecap="round" />
    <line x1="2" y1="6" x2="10" y2="6" stroke={midas.gold} strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════
//  CSS Keyframe Animation (MIDAS-THREADSYNC Phase 2)
// ═══════════════════════════════════════════════════════════════

/** Keyframe name for the new-thread entry animation.
 * Injected once into the document head via useEffect. */
const THREAD_ENTRY_ANIM = "midas-thread-entry";
const THREAD_GOLD_PULSE_ANIM = "midas-thread-gold-pulse";
let animInjected = false;

const injectThreadAnimKeyframes = () => {
  if (animInjected || typeof document === "undefined") return;
  animInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    @keyframes ${THREAD_ENTRY_ANIM} {
      from { opacity: 0; transform: translateY(-8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes ${THREAD_GOLD_PULSE_ANIM} {
      0%   { border-left-color: rgba(212, 168, 67, 1); }
      50%  { border-left-color: rgba(212, 168, 67, 0.3); }
      100% { border-left-color: rgba(212, 168, 67, 0); }
    }
  `;
  document.head.appendChild(style);
};

// ═══════════════════════════════════════════════════════════════
//  Helpers
// ═══════════════════════════════════════════════════════════════

/** Relative time string */
const relativeTime = (iso: string): string => {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
};

// ═══════════════════════════════════════════════════════════════
//  Props
// ═══════════════════════════════════════════════════════════════

interface MachineThreadAccordionProps {
  machineId: string | null;
  isExpanded: boolean;
  threads: Thread[];
  activeThreadId: string | null;
  onThreadClick: (threadId: string) => void;
  onNewChat: () => void;
  onRename: (threadId: string, title: string) => void;
  onDelete: (threadId: string) => void;
  prefersReducedMotion: boolean;
  loading?: boolean;
}

// ═══════════════════════════════════════════════════════════════
//  MachineThreadAccordion
// ═══════════════════════════════════════════════════════════════

const MachineThreadAccordion: React.FC<MachineThreadAccordionProps> = ({
  machineId,
  isExpanded,
  threads,
  activeThreadId,
  onThreadClick,
  onNewChat,
  onRename,
  onDelete,
  prefersReducedMotion,
  loading = false,
}) => {
  const { translate } = useTranslation("chatBot");
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  // MIDAS-THREADSYNC Phase 2: Track IDs that have already animated (to avoid replaying on re-render)
  const animatedIdsRef = useRef<Set<string>>(new Set());

  // Inject CSS keyframes once
  useEffect(() => {
    injectThreadAnimKeyframes();
  }, []);

  // Context menu
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuThreadId, setMenuThreadId] = useState<string | null>(null);

  // Rename dialog
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState("");
  const [renameId, setRenameId] = useState<string | null>(null);
  const renameTitleRef = useRef<HTMLInputElement>(null);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Measure content height for maxHeight animation
  useEffect(() => {
    if (isExpanded && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isExpanded, threads.length]);

  // ── Context menu handlers ──
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>, threadId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setMenuAnchor(e.currentTarget);
      setMenuThreadId(threadId);
    },
    []
  );

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuThreadId(null);
  };

  const handleRenameOpen = () => {
    const thread = threads.find((t) => t.id === menuThreadId);
    if (thread) {
      setRenameId(thread.id);
      setRenameTitle(thread.title);
      setRenameOpen(true);
    }
    handleMenuClose();
  };

  const handleRenameConfirm = () => {
    if (renameId && renameTitle.trim()) {
      onRename(renameId, renameTitle.trim());
    }
    setRenameOpen(false);
    setRenameId(null);
    setRenameTitle("");
  };

  const handleDeleteOpen = () => {
    setDeleteId(menuThreadId);
    setDeleteOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (deleteId) onDelete(deleteId);
    setDeleteOpen(false);
    setDeleteId(null);
  };

  // Focus rename input on open
  useEffect(() => {
    if (renameOpen && renameTitleRef.current) {
      setTimeout(() => renameTitleRef.current?.focus(), 100);
    }
  }, [renameOpen]);

  // Sort threads by updated_at DESC
  const sortedThreads = [...threads].sort(
    (a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
  );

  return (
    <>
      {/* Accordion wrapper with maxHeight animation */}
      <Box
        sx={{
          maxHeight: isExpanded ? `${Math.max(contentHeight, 300)}px` : "0px",
          overflow: "hidden",
          transition: prefersReducedMotion ? "none" : "max-height 200ms ease",
        }}
      >
        <Box ref={contentRef} sx={{ pl: "24px", pr: "4px", pb: "6px" }}>
          {/* "+ New conversation" pinned at top */}
          <Box
            onClick={(e) => { e.stopPropagation(); onNewChat(); }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 8px",
              borderRadius: "6px",
              cursor: "pointer",
              color: midas.gold,
              fontFamily: dt.font,
              fontSize: "11px",
              fontWeight: 500,
              transition: "background-color 150ms",
              "&:hover": { backgroundColor: midas.goldSoft },
            }}
          >
            <PlusIcon />
            {translate("midas.threads.newChat") || "+ Nová konverzácia"}
          </Box>

          {/* Loading state */}
          {loading && threads.length === 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={16} sx={{ color: midas.gold }} />
            </Box>
          )}

          {/* Empty state */}
          {!loading && threads.length === 0 && (
            <Typography
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                color: midas.emptyText,
                textAlign: "center",
                py: "8px",
              }}
            >
              {translate("midas.threads.empty") || "Zatiaľ žiadne konverzácie"}
            </Typography>
          )}

          {/* Thread list */}
          {sortedThreads.map((thread) => {
            const isActive = thread.id === activeThreadId;
            // MIDAS-THREADSYNC Phase 2: Determine if this thread should animate in
            const shouldAnimate = thread.isOptimistic && !prefersReducedMotion && !animatedIdsRef.current.has(thread.id);
            if (shouldAnimate) {
              animatedIdsRef.current.add(thread.id);
            }
            return (
              <Box
                key={thread.id}
                onClick={() => onThreadClick(thread.id)}
                onContextMenu={(e) => handleContextMenu(e, thread.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "7px 8px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  borderLeft: isActive
                    ? `2px solid ${midas.gold}`
                    : "2px solid transparent",
                  backgroundColor: isActive ? midas.goldSoft : "transparent",
                  transition: prefersReducedMotion ? "none" : "background-color 150ms",
                  "&:hover": {
                    backgroundColor: isActive ? midas.goldSoft : dt.rowHover,
                  },
                  "& .thread-menu": { opacity: 0 },
                  "&:hover .thread-menu": { opacity: 1 },
                  // Phase 2 animation: fade + slide + gold pulse (plays once)
                  ...(shouldAnimate ? {
                    animation: `${THREAD_ENTRY_ANIM} 200ms ease-out, ${THREAD_GOLD_PULSE_ANIM} 600ms ease-out 200ms`,
                    animationFillMode: "backwards, forwards",
                  } : {}),
                }}
              >
                <ThreadIcon active={isActive} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      fontWeight: isActive ? 500 : 400,
                      color: isActive ? "#1a1a2e" : midas.inactiveText,
                      lineHeight: 1.3,
                    }}
                  >
                    {thread.title}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: dt.fontMono,
                      fontSize: "9px",
                      color: midas.timestampColor,
                      lineHeight: 1.3,
                    }}
                  >
                    {relativeTime(thread.last_message_at || thread.created_at)}
                  </Typography>
                </Box>
                <IconButton
                  className="thread-menu"
                  onClick={(e) => handleContextMenu(e, thread.id)}
                  size="small"
                  sx={{
                    padding: "2px",
                    color: dt.textMuted,
                    transition: prefersReducedMotion ? "none" : "opacity 150ms",
                  }}
                >
                  <MoreVertRounded sx={{ fontSize: "13px" }} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: dt.cardBg,
            border: `1px solid ${dt.cardBorder}`,
            borderRadius: "8px",
            minWidth: "140px",
          },
        }}
      >
        <MenuItem
          onClick={handleRenameOpen}
          sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textPrimary }}
        >
          <ListItemIcon>
            <EditRounded sx={{ fontSize: "16px", color: dt.textSecondary }} />
          </ListItemIcon>
          <ListItemText>{translate("midas.history.rename") || "Rename"}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={handleDeleteOpen}
          sx={{ fontFamily: dt.font, fontSize: "12px", color: "#ef5350" }}
        >
          <ListItemIcon>
            <DeleteRounded sx={{ fontSize: "16px", color: "#ef5350" }} />
          </ListItemIcon>
          <ListItemText>{translate("midas.history.delete") || "Delete"}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ "& .MuiPaper-root": { backgroundColor: dt.cardBg, borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 600, color: dt.textPrimary }}>
          {translate("midas.history.renameTitle") || "Rename Conversation"}
        </DialogTitle>
        <DialogContent>
          <TextField
            inputRef={renameTitleRef}
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            fullWidth
            size="small"
            onKeyDown={(e) => { if (e.key === "Enter") handleRenameConfirm(); }}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                fontFamily: dt.font, fontSize: "13px", color: dt.textPrimary,
                "& fieldset": { borderColor: dt.cardBorder },
                "&.Mui-focused fieldset": { borderColor: midas.gold },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRenameOpen(false)} sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textMuted }}>
            {translate("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleRenameConfirm}
            disabled={!renameTitle.trim()}
            sx={{ fontFamily: dt.font, fontSize: "12px", color: midas.gold, "&:hover": { backgroundColor: midas.goldSoft } }}
          >
            {translate("midas.history.save") || "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        sx={{ "& .MuiPaper-root": { backgroundColor: dt.cardBg, borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 600, color: dt.textPrimary }}>
          {translate("midas.history.deleteTitle") || "Delete Conversation"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textSecondary }}>
            {translate("midas.history.deleteConfirm") || "Are you sure? This cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textMuted }}>
            {translate("cancel") || "Cancel"}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ fontFamily: dt.font, fontSize: "12px", color: "#ef5350", "&:hover": { backgroundColor: "rgba(239,83,80,0.08)" } }}
          >
            {translate("midas.history.delete") || "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MachineThreadAccordion;
